import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { BankrollMismatchProvider } from './contexts/BankrollMismatchContext';
import { LoadingScreen } from './components/ui/Spinner';
import Header from './components/Header';
import ErrorBoundary from './components/ErrorBoundary';
import BankrollMismatchAlert from './components/BankrollMismatchAlert';
import ActiveSessionAlert from './components/ActiveSessionAlert';
import DashboardPage from './pages/DashboardPage';
import SessionsPage from './pages/SessionsPage';
import ActiveSessionPage from './pages/ActiveSessionPage';
import FinancePage from './pages/FinancePage';
import StatisticsPage from './pages/StatisticsPage';  // Add import
import MistakeTypesPage from './pages/MistakeTypesPage';
import LeaksPage from './pages/LeaksPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import PlayersPage from './components/PlayersPage';
import PlayerDetailsPage from './pages/PlayerDetailsPage';
import SessionDetailsPage from './pages/SessionDetailsPage';
import SessionReviewPage from './pages/SessionReviewPage';
import HandDetailPage from './pages/HandDetailPage';
import HandsPage from './pages/HandsPage';
import DevTrackerPage from './pages/DevTrackerPage';
import BreakSettingsPage from './pages/BreakSettingsPage';
import LocationsPage from './pages/LocationsPage';
import LocationDetailPage from './pages/LocationDetailPage';

function App() {
    const { user, loading } = useAuth();
    const { theme } = useTheme();
    const location = useLocation();

    // Check if we're on login or register pages
    const isAuthPage = location.pathname === '/login' || location.pathname === '/register';

    // Show loading screen while checking authentication
    if (loading) {
        return (
            <div className={theme}>
                <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                    <LoadingScreen />
                </div>
            </div>
        );
    }

    return (
        <div className={theme}>
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
                {!isAuthPage && <Header />}
                {!isAuthPage && user && <BankrollMismatchAlert />}
                {!isAuthPage && user && <ActiveSessionAlert />}
                {isAuthPage ? (
                    <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route path="/register" element={<RegisterPage />} />
                    </Routes>
                ) : (
                    <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8 max-w-7xl mt-0">
                        <Routes>
                            <Route 
                                path="/" 
                                element={user ? <DashboardPage /> : <Navigate to="/login" />}
                            />
                            <Route path="/sessions" element={user ? <SessionsPage /> : <Navigate to="/login" />} />
                            <Route path="/active-session" element={user ? <ActiveSessionPage /> : <Navigate to="/login" />} />
                            <Route path="/sessions/:sessionId" element={user ? <SessionDetailsPage /> : <Navigate to="/login" />} />
                            <Route path="/sessions/:id/review" element={user ? <SessionReviewPage /> : <Navigate to="/login" />} />
                            <Route path="/finance" element={user ? <FinancePage /> : <Navigate to="/login" />} />
                            <Route path="/players" element={user ? <PlayersPage /> : <Navigate to="/login" />} />
                            <Route path="/players/:playerId" element={user ? <PlayerDetailsPage /> : <Navigate to="/login" />} />
                            <Route path="/locations" element={user ? <LocationsPage /> : <Navigate to="/login" />} />
                            <Route path="/locations/:id" element={user ? <LocationDetailPage /> : <Navigate to="/login" />} />
                            <Route path="/statistics" element={user ? <StatisticsPage /> : <Navigate to="/login" />} />
                            {/* Redirect old mistakes routes to hands */}
                            <Route path="/mistakes" element={<Navigate to="/hands" replace />} />
                            <Route path="/mistakes/:mistakeId" element={<Navigate to="/hands" replace />} />
                            <Route path="/mistake-types" element={user ? <MistakeTypesPage /> : <Navigate to="/login" />} />
                            <Route path="/leaks" element={user ? <LeaksPage /> : <Navigate to="/login" />} />
                            <Route path="/hands" element={user ? <HandsPage /> : <Navigate to="/login" />} />
                            <Route path="/hands/:id" element={user ? <HandDetailPage /> : <Navigate to="/login" />} />
                            {/* Break Settings */}
                            <Route path="/break-settings" element={user ? <BreakSettingsPage /> : <Navigate to="/login" />} />
                            {/* Dev Tracker - requires auth */}
                            <Route path="/devtracker" element={user ? <DevTrackerPage /> : <Navigate to="/login" />} />
                            {/* Redirect /dashboard to / */}
                            <Route path="/dashboard" element={<Navigate to="/" replace />} />
                        </Routes>
                    </main>
                )}
            </div>
        </div>
    );
}

// Wrap App with necessary providers
const AppWrapper = () => {
    return (
        <Router>
            <AuthProvider>
                <ThemeProvider>
                    <BankrollMismatchProvider>
                        <ErrorBoundary>
                            <App />
                        </ErrorBoundary>
                    </BankrollMismatchProvider>
                </ThemeProvider>
            </AuthProvider>
        </Router>
    );
};

export default AppWrapper;
